export interface QuickReplyOption {
  label: string;
  value: string;
}

export interface QuickReplyQuestion {
  question: string;
  options: QuickReplyOption[];
  placeholder?: string;
}

export interface ActiveQuickReplies {
  questions: QuickReplyQuestion[];
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object'
);

const stringValue = (value: unknown): string | null => (
  typeof value === 'string' ? value : null
);

export const normalizeQuickReplyOption = (rawOption: unknown): QuickReplyOption | null => {
  let label = '';
  let value = '';

  if (typeof rawOption === 'string') {
    label = rawOption.trim();
    value = label;
  } else if (isRecord(rawOption)) {
    label = stringValue(rawOption.label)?.trim() || '';
    value = stringValue(rawOption.value)?.trim() || label;
  }

  if (!label || !value) return null;

  return {
    label,
    value,
  };
};

export const normalizeQuickReplyQuestion = (rawQuestion: unknown): QuickReplyQuestion | null => {
  if (!isRecord(rawQuestion)) return null;

  const question = stringValue(rawQuestion.question)?.trim() || '';
  const placeholder = stringValue(rawQuestion.placeholder)?.trim() || '';
  const rawOptions = rawQuestion.options;

  if (!question || !Array.isArray(rawOptions)) return null;

  const options: QuickReplyOption[] = [];
  const seenValues = new Set<string>();

  for (const rawOption of rawOptions) {
    const option = normalizeQuickReplyOption(rawOption);
    if (!option || seenValues.has(option.value)) continue;

    seenValues.add(option.value);
    options.push(option);
  }

  return options.length > 0 ? {
    question,
    options,
    ...(placeholder ? { placeholder } : {}),
  } : null;
};

export const normalizeQuickReplyQuestions = (rawQuestions: unknown): QuickReplyQuestion[] => {
  if (!Array.isArray(rawQuestions)) return [];

  const questions: QuickReplyQuestion[] = [];
  for (const rawQuestion of rawQuestions) {
    const question = normalizeQuickReplyQuestion(rawQuestion);
    if (!question) continue;

    questions.push(question);
  }

  return questions;
};

export const quickReplyQuestionsFromMetadata = (metadata: unknown): QuickReplyQuestion[] => {
  if (!isRecord(metadata)) return [];

  const quickReplies = isRecord(metadata.quick_replies) ? metadata.quick_replies : null;
  return normalizeQuickReplyQuestions(quickReplies?.questions);
};

export const quickRepliesFromMessage = (
  message: { message_metadata?: unknown },
): ActiveQuickReplies | null => {
  const questions = quickReplyQuestionsFromMetadata(message.message_metadata);
  return questions.length > 0 ? { questions } : null;
};
